import { useState } from 'react';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { Button, Field, Input } from '../../components/ui';

/**
 * Rename the ONU where the name actually lives: on the chassis.
 *
 * The heading's name is re-read from the chassis by every inventory sweep, so
 * the pencil next to it has to write there — a name saved only in this
 * platform would be overwritten by the next sweep. One save lands it on the
 * chassis (`name` on ZTE, the ONT description on Huawei), on the ONU row, and
 * on the ACS device's alias when the unit has one, so every page agrees once
 * the write settles.
 *
 * It is a chassis write, so — like Resync — the dialog CLOSES on Rename and the
 * work runs in the tasks tray; the toast names the ONU because the dialog is
 * gone by the time the chassis answers.
 */
export function RenameDialog({
  oltId,
  onuId,
  currentName,
  serial,
  href,
  onClose,
}: {
  oltId: string;
  onuId: string;
  currentName: string | null;
  serial: string;
  /** Where the tray row and toast link — the ONU's page. */
  href?: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [name, setName] = useState(currentName ?? '');

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 120;

  const startRename = () => {
    if (!valid) return;
    const next = trimmed;
    onClose();
    tasks.start({
      label: `Rename ${serial}`,
      ...(href ? { href } : {}),
      run: () =>
        api.post(
          `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/rename`,
          { name: next },
        ),
      done: () => `${serial} renamed to “${next}” — written to the chassis.`,
      // Prefix keys: the name shows on the ONU page, every ONU list, the OLT's
      // own ONU table and the ACS device alias.
      invalidate: [['onu'], ['onus'], ['olt'], ['device']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Rename {serial}</h2>
          <p className="mt-0.5 text-xs text-muted">
            Written to the chassis itself, so every system that reads the OLT sees it. A label
            only — the subscriber&rsquo;s service is untouched.
          </p>
        </header>

        <div className="p-5">
          <Field label="Name" hint="Up to 120 characters. The serial stays either way.">
            <Input
              value={name}
              autoFocus
              maxLength={120}
              placeholder="Subscriber name or reference"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') startRename();
                if (e.key === 'Escape') onClose();
              }}
            />
          </Field>
        </div>

        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} onClick={startRename}>
            Rename
          </Button>
        </footer>
      </div>
    </div>
  );
}
